import { type ReactElement } from 'react'
import { type UnsavedQuiz } from '@terros/common'
import { useNavigate, createFileRoute } from '@tanstack/react-router'
import { showNotification } from '@mantine/notifications'
import { useForm } from '@mantine/form'
import { useToastError } from '@hooks'
import { useRemoveQuiz, useTranslation, useUpdateQuiz } from '@front/model'
import { ButtonDelete, ButtonSave, DangerZone, Page } from '@components'
import { QuizPageContainer } from '../quiz_.$quizId/QuizPageContainer'
import { useQuizContext } from '../quiz_.$quizId/context'
import { QuizForm } from './QuizForm'

export const QuizSettingsRoute = (): ReactElement => {
  const { t } = useTranslation()
  const { quiz, refetch, isFetching } = useQuizContext()
  const navigate = useNavigate()
  const { update, isUpdating, error } = useUpdateQuiz()
  const { remove, isRemoving, error: removeError } = useRemoveQuiz()

  useToastError(error)
  useToastError(removeError)

  const form = useForm<UnsavedQuiz>({
    initialValues: {
      ...quiz,
      title: quiz.title === 'Untitled Quiz' ? '' : quiz.title,
      editors: quiz.editors || [],
    },
    validate: {
      title: (value) => (!value ? 'Quiz title is required' : null),
      startDate: (value) => (!value ? 'Activation date is required' : null),
      endDate: (value) => (!value ? 'Deactivation date is required' : null),
      options: {
        minimumScore: (value) => (value === undefined || value === null ? 'Minimum score is required' : null),
        minimumPercentToRevealWrongQuestions: (value) =>
          value === undefined || value === null ? 'Minimum percent is required' : null,
      },
    },
  })

  const handleSubmit = async (values: UnsavedQuiz): Promise<void> => {
    await update({ ...quiz, ...values })
    form.resetDirty(values)
  }

  const handleDelete = async (): Promise<void> => {
    await remove({ quizId: quiz.quizId })
    form.resetDirty()
    showNotification({
      title: t('Quiz Moved to Trash'),
      message: t('You can restore {{name}} anytime from the Trash.', { name: quiz.title }),
      color: 'green',
    })
    navigate({ to: '../..' })
  }

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <QuizPageContainer>
        <Page.Heading isFetching={isFetching} refetch={refetch} title='Quiz Settings'>
          <ButtonSave disabled={isUpdating || !form.isDirty() || !form.isValid()} loading={isUpdating} type='submit' />
        </Page.Heading>
        <Page.Content scrollArea>
          <QuizForm form={form} />
          {!quiz.isDeleted && (
            <DangerZone>
              <ButtonDelete loading={isRemoving} onClick={handleDelete}>
                {t('Move Quiz to Trash')}
              </ButtonDelete>
            </DangerZone>
          )}
        </Page.Content>
      </QuizPageContainer>
    </form>
  )
}

export const Route = createFileRoute('/quiz_/$quizId/settings/')({
  component: QuizSettingsRoute,
})
